import { FormEvent, useState } from 'react';
import { formatClock } from '../MentorAvailabilityPanel';
import type {
    MenteeBookingMode,
    MenteeBookingSlot,
    MenteeCreateBookingBody,
} from './MenteePortalService';

/** Display label → stored DEC-P18-014 code. */
export const BOOKING_MODE_OPTIONS: { value: MenteeBookingMode; label: string }[] = [
    { value: 'ZOOM', label: 'Zoom' },
    { value: 'GOOGLE_MEET', label: 'Google Meet' },
    { value: 'TEAMS', label: 'Teams' },
    { value: 'GOOGLE_CHAT', label: 'Google Chat' },
    { value: 'PHYSICAL_MEET', label: 'Physical Meet' },
    { value: 'OTHERS', label: 'Others' },
];

type Props = {
    mentorName: string | null;
    slot: MenteeBookingSlot;
    submitting: boolean;
    errorMessage: string | null;
    onSubmit: (body: MenteeCreateBookingBody) => void;
    onCancel: () => void;
};

/**
 * Confirm panel — topic as plain free-text (lighter than chips-on-enter;
 * server stores varchar as entered).
 */
const MenteeSessionsConfirmPanel = ({
    mentorName,
    slot,
    submitting,
    errorMessage,
    onSubmit,
    onCancel,
}: Props) => {
    const [topic, setTopic] = useState('');
    const [agenda, setAgenda] = useState('');
    const [mode, setMode] = useState<MenteeBookingMode>('ZOOM');
    const [sessionLink, setSessionLink] = useState('');

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        const trimmedTopic = topic.trim();
        if (!trimmedTopic) return;
        onSubmit({
            date: slot.date,
            startMinute: slot.startMinute,
            topic: trimmedTopic,
            agenda: agenda.trim() || null,
            mode,
            sessionLink: sessionLink.trim() || null,
        });
    };

    return (
        <aside className="msess-confirm-card">
            <h2 className="msess-confirm-title">Confirm booking</h2>
            <div className="msess-confirm-summary">
                <p>
                    <strong>{mentorName || 'Your mentor'}</strong>
                </p>
                <p>
                    {slot.date} · {formatClock(slot.startMinute)} · {slot.durationMinutes} min
                </p>
            </div>
            <form onSubmit={handleSubmit}>
                <div className="msess-field">
                    <label htmlFor="msess-topic">Session Topic (required)</label>
                    <input
                        id="msess-topic"
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        maxLength={512}
                        required
                        placeholder="e.g. Career direction · Case prep"
                    />
                    <p className="msess-hint">Free-text tags as entered (word-cloud style).</p>
                </div>
                <div className="msess-field">
                    <label htmlFor="msess-agenda">Agenda (optional)</label>
                    <textarea
                        id="msess-agenda"
                        value={agenda}
                        onChange={(e) => setAgenda(e.target.value)}
                        placeholder="What would you like to cover?"
                    />
                </div>
                <div className="msess-field">
                    <label htmlFor="msess-mode">Mode</label>
                    <select
                        id="msess-mode"
                        value={mode}
                        onChange={(e) => setMode(e.target.value as MenteeBookingMode)}
                    >
                        {BOOKING_MODE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="msess-field">
                    <label htmlFor="msess-link">Session link (optional)</label>
                    <input
                        id="msess-link"
                        type="url"
                        value={sessionLink}
                        onChange={(e) => setSessionLink(e.target.value)}
                        placeholder="https://"
                    />
                    <p className="msess-hint">URL shape required when provided (http/https).</p>
                </div>
                {errorMessage ? <p className="msess-error">{errorMessage}</p> : null}
                <div className="msess-confirm-actions">
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={submitting || !topic.trim()}
                    >
                        {submitting ? 'Booking…' : 'Confirm Booking'}
                    </button>
                    <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>
                        Cancel
                    </button>
                </div>
            </form>
        </aside>
    );
};

export default MenteeSessionsConfirmPanel;
